// A small, safe math-expression compiler (no eval): tokenizer + Pratt parser → closure.
// Supports + − × ÷ ^, unary minus, implicit multiplication (2x, 3sin(x), (x+1)(x−1)),
// functions, pi / e, and one variable x.

type Node =
  | { t: 'num'; v: number }
  | { t: 'var' }
  | { t: 'neg'; a: Node }
  | { t: 'bin'; op: string; a: Node; b: Node }
  | { t: 'call'; f: string; a: Node };

const FUNCS: Record<string, (x: number) => number> = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  asin: Math.asin,
  acos: Math.acos,
  atan: Math.atan,
  arcsin: Math.asin,
  arccos: Math.acos,
  arctan: Math.atan,
  sinh: Math.sinh,
  cosh: Math.cosh,
  tanh: Math.tanh,
  sec: (x) => 1 / Math.cos(x),
  csc: (x) => 1 / Math.sin(x),
  cot: (x) => 1 / Math.tan(x),
  sqrt: Math.sqrt,
  cbrt: Math.cbrt,
  abs: Math.abs,
  ln: Math.log,
  log: Math.log10,
  log2: Math.log2,
  exp: Math.exp,
  floor: Math.floor,
  ceil: Math.ceil,
  round: Math.round,
  sign: Math.sign,
};

const CONSTS: Record<string, number> = { pi: Math.PI, π: Math.PI, e: Math.E, tau: Math.PI * 2 };

type Tok = { k: 'num'; v: number } | { k: 'id'; v: string } | { k: 'op'; v: string };

function tokenize(src: string): Tok[] {
  const s = src
    .replace(/[×·]/g, '*')
    .replace(/÷/g, '/')
    .replace(/[−–]/g, '-')
    .replace(/²/g, '^2')
    .replace(/³/g, '^3')
    .replace(/√/g, 'sqrt');
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    // Spaces separate tokens (sin 2x, log 2x) but are otherwise ignored.
    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }
    const num = /^(\d+\.?\d*|\.\d+)(e[+-]?\d+)?/i.exec(s.slice(i));
    if (num && !(num[2] && /^e[a-z]/i.test(s.slice(i + num[1].length)))) {
      // "2e" followed by a letter is 2·e·…, not exponent notation.
      const text = num[2] && /^e[+-]?\d/i.test(num[2]) ? num[0] : num[1];
      out.push({ k: 'num', v: Number(text) });
      i += text.length;
      continue;
    }
    // Letters only (plus log2), so sin2x reads as sin·2·x rather than one unknown name.
    const id = /^(log2|[a-zπ]+)/i.exec(s.slice(i));
    if (id) {
      // Split runs like "xsin" or "2pix" into known names greedily.
      let word = id[0].toLowerCase();
      while (word) {
        const name =
          Object.keys(FUNCS)
            .concat(Object.keys(CONSTS), ['x'])
            .filter((n) => word.startsWith(n))
            .sort((a, b) => b.length - a.length)[0] ?? null;
        if (!name) throw new Error(`Unknown name “${word}”. Use x as the variable.`);
        out.push({ k: 'id', v: name });
        word = word.slice(name.length);
        i += name.length;
      }
      continue;
    }
    if ('+-*/^()'.includes(ch)) {
      out.push({ k: 'op', v: ch });
      i += 1;
      continue;
    }
    throw new Error(`Unexpected “${ch}”.`);
  }
  return out;
}

// Implicit multiplication binds like * and /, left to right: 1/2x = (1/2)·x, as Desmos and
// Wolfram read it.
const BP: Record<string, number> = { '+': 10, '-': 10, '*': 20, '/': 20, imp: 20, '^': 30 };

function parse(tokens: Tok[]): Node {
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  // Does the next token start an operand, so that juxtaposition means multiplication?
  const startsOperand = (t: Tok | undefined) =>
    !!t && (t.k === 'num' || t.k === 'id' || (t.k === 'op' && t.v === '('));

  function prefix(): Node {
    const t = next();
    if (!t) throw new Error('The expression ends too early.');
    if (t.k === 'num') return { t: 'num', v: t.v };
    if (t.k === 'id') {
      if (t.v === 'x') return { t: 'var' };
      if (t.v in CONSTS) return { t: 'num', v: CONSTS[t.v] };
      // Function: allow sin(x), sin x, sin^2(x) = (sin x)^2.
      let power: Node | null = null;
      if (peek()?.k === 'op' && peek()?.v === '^') {
        next();
        power = prefix();
      }
      const arg = peek()?.k === 'op' && peek()?.v === '(' ? prefix() : expr(BP.imp, true);
      const call: Node = { t: 'call', f: t.v, a: arg };
      return power ? { t: 'bin', op: '^', a: call, b: power } : call;
    }
    if (t.v === '(') {
      const e = expr(0);
      const close = next();
      if (!close || close.k !== 'op' || close.v !== ')')
        throw new Error('A closing bracket is missing.');
      return e;
    }
    if (t.v === '-') return { t: 'neg', a: expr(BP['*']) };
    if (t.v === '+') return expr(BP['*']);
    throw new Error(`Unexpected “${t.v}”.`);
  }

  // argMode: the bracketless argument of a function (sin 2x) takes juxtaposed factors and
  // powers but stops at an explicit operator: sin 2x/3 = sin(2x)/3.
  function expr(minBp: number, argMode = false): Node {
    let left = prefix();
    for (;;) {
      const t = peek();
      if (!t) break;
      if (t.k === 'op' && t.v === ')') break;
      let op: string;
      if (t.k === 'op' && '+-*/^'.includes(t.v)) op = t.v;
      else if (startsOperand(t)) op = 'imp';
      else break;
      const bp = BP[op];
      if (argMode && op !== 'imp' && op !== '^') break;
      if (!argMode && (bp < minBp || (bp === minBp && op !== '^'))) break;
      if (op !== 'imp') next();
      // ^ is right-associative: 2^3^2 = 2^9.
      const right = expr(op === '^' ? bp : bp + 1);
      left = { t: 'bin', op: op === 'imp' ? '*' : op, a: left, b: right };
    }
    return left;
  }

  const tree = expr(0);
  if (pos < tokens.length) throw new Error('Unexpected “)”.');
  return tree;
}

function build(n: Node): (x: number) => number {
  switch (n.t) {
    case 'num': {
      const v = n.v;
      return () => v;
    }
    case 'var':
      return (x) => x;
    case 'neg': {
      const a = build(n.a);
      return (x) => -a(x);
    }
    case 'call': {
      const f = FUNCS[n.f];
      const a = build(n.a);
      return (x) => f(a(x));
    }
    case 'bin': {
      const a = build(n.a);
      const b = build(n.b);
      switch (n.op) {
        case '+':
          return (x) => a(x) + b(x);
        case '-':
          return (x) => a(x) - b(x);
        case '*':
          return (x) => a(x) * b(x);
        case '/':
          return (x) => a(x) / b(x);
        default:
          return (x) => {
            const base = a(x);
            const p = b(x);
            // Real odd roots of negatives: x^(1/3) at x = −8 is −2, as a graphing calculator shows.
            if (base < 0 && !Number.isInteger(p)) {
              const inv = 1 / p;
              if (
                Number.isInteger(Math.round(inv)) &&
                Math.abs(inv - Math.round(inv)) < 1e-9 &&
                Math.round(inv) % 2
              )
                return -((-base) ** p);
            }
            return base ** p;
          };
      }
    }
  }
}

export function compile(src: string): { fn: (x: number) => number } | { error: string } {
  const body = src.replace(/^\s*(y|f\(x\))\s*=\s*/i, '');
  if (!body.trim()) return { error: 'Type a function of x, like x^2 - 3.' };
  try {
    return { fn: build(parse(tokenize(body))) };
  } catch (e) {
    return { error: (e as Error).message };
  }
}
